'use client';

import Link from 'next/link';
import { Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/card';
import { FreeToolCard } from '@/components/free-tools/free-tool-card';
import {
  FREE_TOOL_CATEGORIES,
  FREE_TOOL_CATEGORY_META,
  freeToolHref,
  type FreeToolCategory,
  type FreeToolDefinition,
} from '@/lib/free-tools/registry';
import { cn } from '@/lib/utils';

/**
 * Searchable, category-grouped browser for the free-tool hub.
 *
 * 24 tools is past the point where a flat wall of cards is scannable, so this
 * pairs the grouping with a filter. Matching runs over label, description, and
 * slug, which is what makes queries like "mp3", "youtube", or "srt" land even
 * when the label does not literally contain them.
 */
/** A pSEO guide page under /tools, flattened by the server component. */
export interface HubGuide {
  href: string;
  title: string;
  description: string;
}

export function ToolsHubBrowser({
  tools,
  guides = [],
}: {
  tools: FreeToolDefinition[];
  guides?: HubGuide[];
}) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<FreeToolCategory | 'all' | 'guides'>('all');

  const q = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (category === 'guides') return [];

    return tools.filter((tool) => {
      if (category !== 'all' && tool.category !== category) return false;

      if (!q) return true;

      return (
        tool.label.toLowerCase().includes(q) ||
        tool.description.toLowerCase().includes(q) ||
        tool.slug.replace(/-/g, ' ').includes(q)
      );
    });
  }, [tools, q, category]);

  // Guides live at /tools/* too, so one query has to reach them as well.
  const filteredGuides = useMemo(() => {
    if (category !== 'all' && category !== 'guides') return [];

    if (!q) return guides;

    return guides.filter(
      (guide) =>
        guide.title.toLowerCase().includes(q) ||
        guide.description.toLowerCase().includes(q) ||
        guide.href.replace(/[-/]/g, ' ').includes(q)
    );
  }, [guides, q, category]);

  const groups = useMemo(
    () =>
      FREE_TOOL_CATEGORIES.map((key) => ({
        key,
        ...FREE_TOOL_CATEGORY_META[key],
        tools: filtered.filter((tool) => tool.category === key),
      })).filter((group) => group.tools.length > 0),
    [filtered]
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-md">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--foreground-secondary))]"
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search tools, e.g. subtitles, mp3, YouTube"
            aria-label="Search free tools"
            className="min-h-[44px] w-full rounded-full border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] py-2 pl-10 pr-10 text-sm text-[rgb(var(--foreground))] outline-none transition-colors placeholder:text-[rgb(var(--foreground-secondary))] focus:border-[#8B5CF6]"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[rgb(var(--foreground-secondary))] transition-colors hover:text-[rgb(var(--foreground))]"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          {(['all', ...FREE_TOOL_CATEGORIES, 'guides'] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setCategory(key)}
              aria-pressed={category === key}
              className={cn(
                // min-h-11 (44px), not an arbitrary value: the chips were
                // rendering at 34px because `min-h-[36px]` never generated, and
                // 34px is under the touch-target floor regardless.
                'min-h-11 rounded-full border px-4 py-2 text-sm font-medium transition-colors',
                category === key
                  ? 'border-[#8B5CF6] bg-[#8B5CF6]/10 text-[#8B5CF6]'
                  : 'border-[rgb(var(--surface-border))] text-[rgb(var(--foreground-secondary))] hover:text-[rgb(var(--foreground))]'
              )}
            >
              {key === 'all'
                ? 'All'
                : key === 'guides'
                  ? 'Guides'
                  : FREE_TOOL_CATEGORY_META[key].label}
            </button>
          ))}
        </div>
      </div>

      <p aria-live="polite" className="sr-only">
        {filtered.length + filteredGuides.length} results match
      </p>

      {groups.length === 0 && filteredGuides.length === 0 ? (
        <div className="rounded-xl border border-[rgb(var(--surface-border))] p-10 text-center">
          <p className="text-[rgb(var(--foreground))]">No tool matches &ldquo;{query}&rdquo;.</p>
          <p className="mt-2 text-sm text-[rgb(var(--foreground-secondary))]">
            Try a format (mp3, srt), a platform (YouTube, Vimeo), or{' '}
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setCategory('all');
              }}
              className="underline underline-offset-2 hover:text-[rgb(var(--foreground))]"
            >
              clear the filters
            </button>
            .
          </p>
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.key} className="space-y-5">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-[rgb(var(--foreground))]">
                {group.label}
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-[rgb(var(--foreground-secondary))]">
                {group.blurb}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {group.tools.map((tool) => (
                <FreeToolCard key={tool.slug} tool={tool} surface="hub_card" />
              ))}
            </div>
          </section>
        ))
      )}

      {filteredGuides.length > 0 ? (
        <section className="space-y-5 border-t border-[rgb(var(--surface-border))] pt-10">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-[rgb(var(--foreground))]">
              Free transcription guides
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-[rgb(var(--foreground-secondary))]">
              Written walkthroughs for specific formats and workflows. Each one links to the live
              free converter.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredGuides.map((guide) => (
              <Link key={guide.href} href={guide.href}>
                <Card
                  variant="elevated"
                  className="group flex h-full flex-col p-6 transition-all hover:shadow-xl"
                >
                  <CardHeader className="mb-2 p-0">
                    <CardTitle className="text-lg transition-colors group-hover:text-[#8B5CF6]">
                      {guide.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col p-0">
                    <p className="line-clamp-3 text-sm leading-relaxed text-[rgb(var(--foreground-secondary))]">
                      {guide.description}
                    </p>
                    <span className="mt-4 text-sm font-medium text-[#8B5CF6]">Read more →</span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
